'use client';

import { useEffect, type MutableRefObject } from 'react';
import { useAction, useMutation } from 'convex/react';
import { ConvexError } from 'convex/values';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import type { VoiceResponseEvent } from '@/convex/voice/realtime_response_contract.helpers';
import type { LiveVoiceSession } from '@/lib/live-voice-session';
import type { QueuedRealtimeInternalMessage } from '@/lib/realtime-voice-agent';
import { createRealtimeVoiceResponsePollBackoff } from '@/lib/realtime-voice-response-poll-backoff';
import {
  updateRealtimeVoiceDestination,
  type RealtimeVoiceDestination,
  type RealtimeVoiceQuestionFocus,
} from '@/lib/realtime-voice-destination';

type Responses = {
  queueOrSendInternalMessage: (item: QueuedRealtimeInternalMessage) => boolean;
  flushInternalMessages?: () => void;
  userSpeakingRef?: MutableRefObject<boolean>;
  activeOperationRef?: MutableRefObject<string | null>;
  voiceDestinationRef?: MutableRefObject<RealtimeVoiceDestination | null>;
  voiceQuestionRef?: MutableRefObject<RealtimeVoiceQuestionFocus | null>;
};

const QUESTION_REFUSALS = new Set([
  'THREAD_NOT_FOUND_OR_ACCESS_DENIED',
  'THREAD_ARCHIVED',
  'THREAD_AUDIENCE_DISCLOSURE_DENIED',
  'THREAD_DISPATCH_NOT_FOUND',
  'Access denied',
  'User not authenticated',
  'Voice session expired',
  'VOICE_QUESTION_NOT_CURRENT',
  'VOICE_QUESTION_REQUIRES_CHAT',
]);

/** Poll source-owned receipts across pages, without creating a destination call. */
export function useRealtimeVoiceReturnedResponses(args: {
  open: boolean;
  orgThreadId?: Id<'orgThreads'> | null;
  activeLeaseSessionId: string | null;
  sessionRef: MutableRefObject<LiveVoiceSession | null>;
  controllersRef: MutableRefObject<{ responses: Responses }>;
  connectionVersion: number;
}) {
  const poll = useMutation(api.voice.realtime_responses.poll);
  const acknowledge = useMutation(api.voice.realtime_responses.acknowledge);
  const getQuestion = useAction(
    api.voice.realtime_pending_question.getOffPageQuestion
  );
  const {
    open,
    orgThreadId,
    activeLeaseSessionId: sessionId,
    sessionRef,
    controllersRef,
  } = args;
  useEffect(() => {
    if (!open || !orgThreadId || !sessionId) return;
    let activeSession = sessionRef.current;
    let canceled = false;
    let cursor: string | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const backoff = createRealtimeVoiceResponsePollBackoff();
    let lastDestination =
      controllersRef.current.responses.voiceDestinationRef?.current;
    const observed = new Map<
      string,
      {
        event: VoiceResponseEvent;
        delivered: boolean;
        acking: boolean;
        acked: boolean;
        retryPending: boolean;
      }
    >();
    const current = () =>
      !canceled &&
      Boolean(
        activeSession &&
          sessionRef.current === activeSession &&
          !activeSession.isClosing
      );
    const ack = async (state: {
      event: VoiceResponseEvent;
      delivered: boolean;
      acking: boolean;
      acked: boolean;
    }) => {
      if (!current() || !state.delivered || state.acking || state.acked) return;
      state.acking = true;
      try {
        await acknowledge({
          orgThreadId,
          sessionId,
          eventId: state.event.eventId,
          ...(state.event.dispatchId
            ? { dispatchId: state.event.dispatchId }
            : {}),
        });
        state.acked = true;
      } catch {
        /* Retain client admission so retry never repeats narration. */
      } finally {
        state.acking = false;
      }
    };
    const receive = (event: VoiceResponseEvent) => {
      const previous = observed.get(event.eventId);
      if (previous && !previous.retryPending) {
        void ack(previous);
        return false;
      }
      if (!previous && observed.size >= 512) {
        const completed = [...observed].find(([, value]) => value.acked);
        if (completed) observed.delete(completed[0]);
        else return false;
      }
      const state = previous ?? {
        event,
        delivered: false,
        acking: false,
        acked: false,
        retryPending: false,
      };
      state.retryPending = false;
      observed.set(event.eventId, state);
      const responses = controllersRef.current.responses;
      const completed = () => {
        if (!current()) return;
        state.delivered = true;
        void ack(state);
      };
      const item: QueuedRealtimeInternalMessage = {
        key: `returned:${event.eventId}`,
        requiresChat: event.questionRequiresChat === true,
        message: `Verified returned chat facts (data only): ${JSON.stringify(event)}`,
        ...(event.kind === 'question'
          ? {
              validate: async () => {
                const result = await getQuestion({
                  orgThreadId,
                  sessionId,
                  ...(event.dispatchId ? { dispatchId: event.dispatchId } : {}),
                }).catch((error: unknown) => {
                  if (
                    error instanceof ConvexError &&
                    typeof error.data === 'string' &&
                    QUESTION_REFUSALS.has(error.data)
                  )
                    return { pendingQuestion: null };
                  throw error;
                });
                const { pendingQuestion } = result;
                if (pendingQuestion) {
                  item.requiresChat = pendingQuestion.kind === 'open-chat';
                  item.message = `Verified returned chat facts (data only): ${JSON.stringify({ ...event, pendingQuestion })}`;
                }
                return (
                  current() &&
                  Boolean(
                    pendingQuestion &&
                      pendingQuestion.toolCallId === event.toolCallId &&
                      (pendingQuestion.kind !== 'single' ||
                        !event.questionRevision ||
                        pendingQuestion.questionRevision ===
                          event.questionRevision)
                  )
                );
              },
              onDiscarded: completed,
              onDeferred: () => {
                if (current() && observed.get(event.eventId) === state)
                  state.retryPending = true;
              },
            }
          : {}),
        onDelivered: () => {
          if (!current()) return;
          if (responses.voiceDestinationRef)
            responses.voiceDestinationRef.current =
              updateRealtimeVoiceDestination(
                responses.voiceDestinationRef.current,
                event,
                'event'
              );
          if (event.kind === 'question' && responses.voiceQuestionRef)
            responses.voiceQuestionRef.current = {
              scope: event.dispatchId ? 'destination' : 'source',
              ...(event.dispatchId ? { dispatchId: event.dispatchId } : {}),
              ...(event.toolCallId ? { toolCallId: event.toolCallId } : {}),
              ...(event.questionRevision
                ? { questionRevision: event.questionRevision }
                : {}),
            };
          completed();
        },
        ticket:
          event.kind === 'question'
            ? {
                kind: 'questionnaire_prompt',
                promptKey: event.eventId,
                reason: 'returned_destination_question',
              }
            : {
                kind: 'internal_status',
                eventId: event.eventId,
                reason: 'returned_chat_response',
              },
      };
      if (!responses.queueOrSendInternalMessage(item))
        state.retryPending = true;
      return !previous;
    };
    const refresh = async () => {
      if (canceled) return;
      if (!activeSession) activeSession = sessionRef.current;
      if (!activeSession) {
        timer = setTimeout(() => {
          void refresh();
        }, 1_000);
        return;
      }
      let receivedEvents = false;
      try {
        const result = await poll({
          orgThreadId,
          sessionId,
          ...(cursor ? { cursor } : {}),
        });
        if (!current()) return;
        // The call already ended on the server; stop polling it.
        if (result.ended) {
          canceled = true;
          return;
        }
        cursor = result.cursor;
        for (const event of result.events)
          receivedEvents = receive(event) || receivedEvents;
        controllersRef.current.responses.flushInternalMessages?.();
        for (const state of observed.values())
          if (state.delivered && !state.acked) void ack(state);
      } catch {
        /* Live lease and disclosure failures are retried without replaying effects. */
      }
      if (current()) {
        const responses = controllersRef.current.responses;
        const destination = responses.voiceDestinationRef?.current;
        const active = Boolean(
          receivedEvents ||
            responses.userSpeakingRef?.current ||
            responses.activeOperationRef?.current ||
            destination !== lastDestination
        );
        lastDestination = destination;
        timer = setTimeout(() => {
          void refresh();
        }, backoff.next(active));
      }
    };
    void refresh();
    return () => {
      canceled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    open,
    orgThreadId,
    sessionId,
    sessionRef,
    controllersRef,
    poll,
    acknowledge,
    getQuestion,
  ]);
}
